import { Badge, Box, Button, Flex, Text, Image, Grid } from "@chakra-ui/react";
import { IconArrowSingleRight } from "components/icons/Icons";
import { ProjectRowData } from "../table/types";
import LanguageIcon from "assets/img/dashboards/language.svg";

interface ProjectCardProps {
  project: ProjectRowData;
  onViewDetails: (orderId: string) => void;
  tabIndex?: number;
}

const getTabStatus = (tabIdx: number) => {
  if (tabIdx === 0) return "Active";
  if (tabIdx === 1) return "Completed";
  return "Draft";
};

const getStatusColors = (status: string | null) => {
  if (!status) return { bg: "#D2DAE5", text: "#0B0E10" };

  const lowerStatus = status.toLowerCase();
  if (lowerStatus === "active") return { bg: "#86EFAC", text: "#0B0E10" };
  if (lowerStatus === "completed") return { bg: "#B8D0FF", text: "#1E3A8A" };
  if (lowerStatus === "draft") return { bg: "#D2DAE5", text: "#0B0E10" };
  if (lowerStatus === "revision" || lowerStatus === "review")
    return { bg: "#DBEAFE", text: "#1E40AF" };
  return { bg: "#D2DAE5", text: "#0B0E10" };
};

export const ProjectCard = ({
  project,
  onViewDetails,
  tabIndex = 0,
}: ProjectCardProps) => {
  const displayStatus = getTabStatus(tabIndex);
  const statusColors = getStatusColors(displayStatus);

  const handleViewDetails = () => {
    onViewDetails(project.order_id);
  };
  return (
    <Box
      bg="#F5F7FA"
      borderRadius="16px"
      border="1px solid"
      borderColor="#E6ECF3"
      p="24px"
      minH="220px"
      display="flex"
      flexDirection="column"
      transition="all 0.2s"
      _hover={{
        bg: "#E8EDF2",
      }}
      sx={{
        "& .fields-grid": {
          gridTemplateColumns: "50% 50%",
        },
        "@media (max-width: 526px)": {
          "& .fields-grid": {
            gridTemplateColumns: "1fr",
          },
        },
      }}
    >
      {/* Header: Project ID + Status Badge */}
      <Flex justify="space-between" align="center" mb="8px">
        <Text fontSize="18px" fontWeight="600" color="#0B0E10" lineHeight="1">
          {String(project.id).padStart(6, "0")}
        </Text>
        <Badge
          bg={statusColors.bg}
          color={statusColors.text}
          fontSize="14px"
          fontWeight="500"
          py="8px"
          borderRadius="8px"
          textTransform="capitalize"
        >
          {displayStatus}
        </Badge>
      </Flex>

      {/* Language Info */}
      <Flex align="center" gap="8px" mb="8px">
        <Text
          color="#0B0E10"
          fontSize="16px"
          lineHeight="24px"
          fontWeight="400"
          whiteSpace="nowrap"
        >
          {project.translated_from} → {project.translated_to}
        </Text>
      </Flex>

      {/* Type and Pages Row */}
      <Grid className="fields-grid" gap="0" mb="8px">
        <Flex align="center" gap="8px">
          <Text
            color="#6B7280"
            fontSize="14px"
            lineHeight="18px"
            fontWeight="400"
            whiteSpace="nowrap"
          >
            Type:
          </Text>
          <Text
            color="#0B0E10"
            fontSize="14px"
            lineHeight="18px"
            fontWeight="600"
            textTransform="capitalize"
            whiteSpace="nowrap"
          >
            {project.translation_type}
          </Text>
        </Flex>
        <Flex align="center" gap="8px">
          <Text
            color="#6B7280"
            fontSize="14px"
            lineHeight="18px"
            fontWeight="400"
            whiteSpace="nowrap"
          >
            Pages:
          </Text>
          <Text
            color="#0B0E10"
            fontSize="14px"
            lineHeight="18px"
            fontWeight="600"
            whiteSpace="nowrap"
          >
            {project.page_count}
          </Text>
        </Flex>
      </Grid>

      {/* Due Date and Price Row */}
      <Grid className="fields-grid" gap="0">
        <Flex align="center" gap="8px">
          <Text
            color="#6B7280"
            fontSize="14px"
            lineHeight="18px"
            fontWeight="400"
            whiteSpace="nowrap"
          >
            Due date:
          </Text>
          <Text
            color="#0B0E10"
            fontSize="14px"
            lineHeight="18px"
            fontWeight="600"
            whiteSpace="nowrap"
          >
            {project.order_due_at ? project.order_due_at.split(" ")[0] : "-"}
          </Text>
        </Flex>
        <Flex align="center" gap="8px">
          <Text
            color="#6B7280"
            fontSize="14px"
            lineHeight="18px"
            fontWeight="400"
            whiteSpace="nowrap"
          >
            Price:
          </Text>
          <Text
            color="#0B0E10"
            fontSize="14px"
            lineHeight="18px"
            fontWeight="600"
            whiteSpace="nowrap"
          >
            ${project.order_total}
          </Text>
        </Flex>
      </Grid>

      {/* View Details Button */}
      <Button
        mt="14px"
        w="100%"
        h="40px"
        minH="40px"
        bg="transparent"
        color="#3A7BFF"
        border="1px solid"
        borderColor="#3A7BFF"
        fontSize="14px"
        fontWeight="500"
        lineHeight="1"
        borderRadius="32px"
        px="16px"
        py="0"
        rightIcon={
          <IconArrowSingleRight
            w="34px"
            h="34px"
            sx={{ path: { fill: "currentColor" } }}
          />
        }
        _hover={{
          bg: "#3A7BFF",
          color: "white",
          borderColor: "#3A7BFF",
        }}
        onClick={handleViewDetails}
      >
        View Details
      </Button>
    </Box>
  );
};
